import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

import { uniqueDeliveryContractCodes } from '~/models/unknownIssuerBid'
import type { SpotTabKey } from '~/pages/SpotBidding/constants/tabs'

import { type BlockBidRow, isUnknownIssuerBlockRow } from './blockBidRow'
import type { SpotBidRow } from './spotBidRow'

/**
 * 警告 (AC-BSP-111b) に並べる受渡契約コード。 表示中の受渡日の通常の札と、 発注主体区分が
 * 空欄のブロックグループの全 leg から集める。 ステータスは問わない (#10084)。
 */
export const spotUnknownIssuerDeliveryContractCodes = (
  bids: readonly SpotBidRow[],
  blockRows: readonly BlockBidRow[],
): string[] =>
  uniqueDeliveryContractCodes([
    ...bids
      .filter((b) => b.issuerType === 'unknown')
      .map((b) => b.deliveryContractCode),
    ...blockRows
      .filter(isUnknownIssuerBlockRow)
      .map((r) => r.deliveryContractCode),
  ])

/**
 * 空状態パネル (AC-BSP-105) の「発注主体区分が分からない入札が{件数}件あります。」 の件数。
 * 表示中のタブの、 区分が空欄の札のうち業務エリアにあるものを数える。 ブロック入札は
 * グループを 1 件とし、 LINK / LOOP はいずれかの Leg が業務エリアにあれば数える
 * (AC-BSP-019 の一覧のグループ表示と同じ)。 札を持たないタブは 0。
 */
export const spotUnknownIssuerBidCount = ({
  tab,
  bids,
  blockRows,
  businessAreas,
}: {
  tab: SpotTabKey
  bids: ReadonlyArray<Pick<SpotBidRow, 'issuerType' | 'areaCode'>>
  blockRows: ReadonlyArray<
    Pick<BlockBidRow, 'groupId' | 'issuerType' | 'areaCode'>
  >
  businessAreas: readonly AreaCode[]
}): number => {
  const inBusinessArea = (area: AreaCode): boolean =>
    businessAreas.includes(area)
  if (tab === 'normalBid') {
    return bids.filter(
      (b) => b.issuerType === 'unknown' && inBusinessArea(b.areaCode),
    ).length
  }
  if (tab === 'blockBid') {
    return new Set(
      blockRows
        .filter((r) => isUnknownIssuerBlockRow(r) && inBusinessArea(r.areaCode))
        .map((r) => r.groupId),
    ).size
  }
  return 0
}
